import { useEffect, useState } from "react";
import { toast } from "sonner";
import { 
  Plus, 
  Pencil, 
  Trash2, 
  Factory,
  Save
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { getAllProduttori, createProduttore, updateProduttore, deleteProduttore } from "@/services/localDatabase";

const Produttori = () => {
  const [produttori, setProduttori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [formData, setFormData] = useState({
    nome: "",
    sede_legale: "",
    cap: "",
    citta: "",
    provincia: "",
    ric: "",
    email: "",
    serie_ddr: "AP"
  });

  useEffect(() => {
    fetchProduttori();
  }, []);

  const fetchProduttori = async () => {
    try {
      setLoading(true);
      const data = await getAllProduttori();
      setProduttori(data);
    } catch (error) {
      console.error("Error fetching produttori:", error);
      toast.error("Errore nel caricamento dei produttori");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenNew = () => {
    setEditingId(null);
    setFormData({
      nome: "",
      sede_legale: "",
      cap: "",
      citta: "",
      provincia: "",
      ric: "",
      email: "",
      serie_ddr: "AP"
    });
    setShowDialog(true);
  };

  const handleEdit = (prod) => {
    setEditingId(prod.id);
    setFormData({
      nome: prod.nome || "",
      sede_legale: prod.sede_legale || "",
      cap: prod.cap || "",
      citta: prod.citta || "",
      provincia: prod.provincia || "",
      ric: prod.ric || "",
      email: prod.email || "",
      serie_ddr: prod.serie_ddr || "AP"
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateProduttore(editingId, formData);
        toast.success("Produttore aggiornato");
      } else {
        await createProduttore(formData);
        toast.success("Produttore creato");
      }
      setShowDialog(false);
      fetchProduttori();
    } catch (error) {
      console.error("Error saving produttore:", error);
      toast.error("Errore nel salvataggio");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await deleteProduttore(deleteId);
      toast.success("Produttore eliminato");
      fetchProduttori();
    } catch (error) {
      console.error("Error deleting produttore:", error);
      toast.error("Errore nell'eliminazione");
    } finally {
      setDeleteId(null);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="p-6 md:p-8 lg:p-12 fade-in" data-testid="produttori-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-slate-900 font-barlow tracking-tight">
            Archivio Produttori
          </h1>
          <p className="text-slate-500 mt-1">Gestisci i produttori per i documenti DDR</p>
        </div>
        <Button 
          onClick={handleOpenNew}
          className="mt-4 md:mt-0 bg-orange-500 hover:bg-orange-600 text-white font-bold uppercase tracking-wide h-11 px-8"
          data-testid="new-produttore-btn"
        >
          <Plus className="w-5 h-5 mr-2" />
          Nuovo Produttore
        </Button>
      </div>

      {/* List */}
      <Card className="border-slate-100" data-testid="produttori-list">
        <CardHeader className="border-b border-slate-100 bg-slate-50/50">
          <CardTitle className="font-barlow text-lg font-semibold text-slate-900">
            Produttori ({produttori.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500">Caricamento...</div>
          ) : produttori.length === 0 ? (
            <div className="p-8 text-center">
              <Factory className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">Nessun produttore salvato</p>
              <Button 
                onClick={handleOpenNew}
                className="mt-4 bg-orange-500 hover:bg-orange-600"
              >
                Aggiungi il primo produttore
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Nome / Ragione Sociale</th>
                    <th>Sede Legale</th>
                    <th>Città</th>
                    <th>RIC O.I.</th>
                    <th>Serie DDR</th>
                    <th>Azioni</th>
                  </tr>
                </thead>
                <tbody>
                  {produttori.map((prod) => (
                    <tr key={prod.id} data-testid={`prod-row-${prod.id}`}>
                      <td className="font-medium">{prod.nome}</td>
                      <td className="max-w-[200px] truncate">{prod.sede_legale}</td>
                      <td>{prod.citta} ({prod.provincia})</td>
                      <td className="font-mono text-sm">{prod.ric}</td>
                      <td>
                        <Badge variant="outline">{prod.serie_ddr}</Badge>
                      </td>
                      <td>
                        <div className="flex gap-1">
                          <Button
                            data-testid={`edit-prod-${prod.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(prod)}
                            className="text-slate-600 hover:text-orange-500"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            data-testid={`delete-prod-${prod.id}`}
                            variant="ghost"
                            size="sm"
                            onClick={() => setDeleteId(prod.id)}
                            className="text-slate-600 hover:text-red-500"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle className="font-barlow text-xl">
              {editingId ? "Modifica Produttore" : "Nuovo Produttore"}
            </DialogTitle>
            <DialogDescription>
              Inserisci i dati del produttore per i documenti DDR
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium text-slate-700">
                Nome / Ragione Sociale
              </Label>
              <Input
                value={formData.nome}
                onChange={(e) => handleChange("nome", e.target.value)}
                placeholder="es. ANDREA PADUANO"
                className="mt-1"
                data-testid="prod-nome"
              />
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">
                Sede Legale (Via e Numero)
              </Label>
              <Input
                value={formData.sede_legale}
                onChange={(e) => handleChange("sede_legale", e.target.value)}
                placeholder="es. VIA SPINA, 65"
                className="mt-1"
                data-testid="prod-sede"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">CAP</Label>
                <Input
                  value={formData.cap}
                  onChange={(e) => handleChange("cap", e.target.value)}
                  placeholder="44022"
                  className="mt-1"
                  data-testid="prod-cap"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Città</Label>
                <Input
                  value={formData.citta}
                  onChange={(e) => handleChange("citta", e.target.value)}
                  placeholder="COMACCHIO"
                  className="mt-1"
                  data-testid="prod-citta"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Prov.</Label>
                <Input
                  value={formData.provincia}
                  onChange={(e) => handleChange("provincia", e.target.value)}
                  placeholder="FE"
                  className="mt-1"
                  maxLength={2}
                  data-testid="prod-provincia"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm font-medium text-slate-700">RIC O.I.</Label>
                <Input
                  value={formData.ric}
                  onChange={(e) => handleChange("ric", e.target.value)}
                  placeholder="1338/2023"
                  className="mt-1"
                  data-testid="prod-ric"
                />
              </div>
              <div>
                <Label className="text-sm font-medium text-slate-700">Serie DDR</Label>
                <Input
                  value={formData.serie_ddr}
                  onChange={(e) => handleChange("serie_ddr", e.target.value.toUpperCase())}
                  placeholder="AP"
                  className="mt-1"
                  maxLength={3}
                  data-testid="prod-serie"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium text-slate-700">Email</Label>
              <Input
                value={formData.email}
                onChange={(e) => handleChange("email", e.target.value)}
                placeholder="email@example.com"
                className="mt-1"
                type="email"
                data-testid="prod-email"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annulla
            </Button>
            <Button 
              onClick={handleSave}
              className="bg-orange-500 hover:bg-orange-600"
              data-testid="save-prod-btn"
            >
              <Save className="w-4 h-4 mr-2" />
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare questo produttore? L'azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-prod">Annulla</AlertDialogCancel>
            <AlertDialogAction
              data-testid="confirm-delete-prod"
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Produttori;
